import { useRef, type ReactNode } from 'react'
import { Button, type State } from './controls'
import { Icon, type IconName } from './Icon'

/*
 * Banners, toasts, empty states, dialogs, plan meters: wrappers of the Strevix class layer
 * (cards ToastsAndBanners, EmptyStates, ConfirmDialog, PlansAndLimits, UsageMeters).
 * Toasts only for "saved"-type confirmations and quota warnings (owner's decision 23.10); incidents
 * and maintenance are banners; destructive confirmations stay inline or in a Dialog.
 */

type Tone = Exclude<State, 'disabled'>
const role = (s: Tone) => (s === 'fail' ? 'alert' : 'status')

/** Full width under the top bar: incidents, maintenance, resolutions. */
export function Banner({ state, time, action, children }: { state: Tone; time?: ReactNode; action?: ReactNode; children: ReactNode }) {
  return (
    <div className="sx-banner" data-state={state} role={role(state)}>
      <Icon name={`state-${state}`} size={16} />
      <span className="txt">{children}</span>
      {time && <span className="tm">{time}</span>}
      {action}
    </div>
  )
}

/** A short confirmation in the corner; `closeLabel` is the close button's accessible name. */
export function Toast({ state, title, body, action, onClose, closeLabel }: { state: Tone; title: ReactNode; body?: ReactNode; action?: ReactNode; onClose: () => void; closeLabel: string }) {
  return (
    <div className="sx-toast" data-state={state} role={role(state)}>
      <Icon name={`state-${state}`} />
      <div>
        <div className="h">{title}</div>
        {body && <div className="b">{body}</div>}
        {action && <div className="a">{action}</div>}
      </div>
      <Button variant="ghost" size="sm" icon aria-label={closeLabel} onClick={onClose}>
        <Icon name="close" size={16} />
      </Button>
    </div>
  )
}

/** What is not there yet, and the one thing to do about it. */
export function Empty({ icon, title, children, action }: { icon: IconName; title: ReactNode; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="sx-empty">
      <div className="ic">
        <Icon name={icon} size={24} />
      </div>
      <h4>{title}</h4>
      {children && <p>{children}</p>}
      {action}
    </div>
  )
}

/**
 * Confirmation dialog over a scrim (role="alertdialog"); the caller owns focus and Escape.
 * `modal` covers the viewport. Width stays the system's 440 px unless `width` is set.
 */
export function Dialog({ id, state, title, children, footer, width, busy, modal, testId }: { id: string; state?: 'warn' | 'fail'; title: ReactNode; children: ReactNode; footer: ReactNode; width?: number; busy?: boolean; modal?: boolean; testId?: string }) {
  return (
    <div className={modal ? 'sx-scrim fixed inset-0 z-[60] overflow-y-auto !flex !items-start !justify-center !rounded-none' : 'sx-scrim'}>
      {/* margin auto: centred while it fits, scrolls from the top when a form is taller than the screen */}
      <div className={modal ? 'sx-dialog my-auto !w-[min(440px,100%)]' : 'sx-dialog'} role="alertdialog" aria-modal="true" aria-busy={busy || undefined} aria-labelledby={`${id}-title`} data-testid={testId} data-state={state} style={width ? { width } : undefined}>
        <div className="hd">
          {state && <Icon name={`state-${state}`} size={24} className={state === 'fail' ? 'text-fail' : 'text-warn'} />}
          <h3 id={`${id}-title`}>{title}</h3>
        </div>
        <div className="bd">{children}</div>
        <div className="ft">{footer}</div>
      </div>
    </div>
  )
}

/** Six cells of a confirmation code (ConfirmDialog). Paste of all digits fills every cell. */
export function CodeCells({ id, value, onChange, invalid, label }: { id: string; value: string[]; onChange: (v: string[]) => void; invalid?: boolean; label: string }) {
  const refs = useRef<Array<HTMLInputElement | null>>([])
  const cells = Array.from({ length: 6 }, (_, i) => value[i] ?? '')
  const write = (index: number, raw: string) => {
    const digits = raw.replace(/\D/g, '')
    const next = cells.slice()
    if (digits.length > 1) {
      for (let k = 0; k < digits.length && index + k < 6; k++) next[index + k] = digits[k]!
      onChange(next)
      refs.current[Math.min(5, index + digits.length)]?.focus()
      return
    }
    next[index] = digits.slice(-1)
    onChange(next)
    if (digits && index < 5) refs.current[index + 1]?.focus()
  }
  return (
    <div className="sx-field" data-state={invalid ? 'fail' : undefined}>
      <span className="lbl" id={`${id}-label`}>{label}</span>
      <div className="sx-otp" role="group" aria-labelledby={`${id}-label`} data-testid={id}>
        {cells.map((ch, i) => (
          <input
            key={i}
            ref={(el) => {
              refs.current[i] = el
            }}
            className={`w-[36px] h-10 rounded-md border bg-bg text-center font-mono text-lg text-text tabular-nums slashed-zero focus:outline-none focus-visible:shadow-focus ${invalid ? 'border-fail' : 'border-line-strong'}`}
            inputMode="numeric"
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            aria-label={`Digit ${i + 1} of 6`}
            value={ch}
            onChange={(e) => write(i, e.target.value)}
            onPaste={(e) => {
              const text = e.clipboardData.getData('text')
              if (!/\d/.test(text)) return
              e.preventDefault()
              write(i, text)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Backspace' && !cells[i] && i > 0) {
                e.preventDefault()
                const next = cells.slice()
                next[i - 1] = ''
                onChange(next)
                refs.current[i - 1]?.focus()
              }
            }}
          />
        ))}
      </div>
    </div>
  )
}

/** Counted limit (outputs on the plan): one segment per unit. `count` is the caller's "3 of 10". */
export function Meter({ label, used, max, count, state, note }: { label: ReactNode; used: number; max: number; count: ReactNode; state?: 'warn' | 'fail'; note?: ReactNode }) {
  return (
    <div className="sx-meter" data-state={state ?? ''}>
      <div className="top">
        <span>{label}</span>
        <b>{count}</b>
      </div>
      <div className="trk">
        {Array.from({ length: max }, (_, i) => (
          <i key={i} className={i < used ? 'u' : ''} />
        ))}
      </div>
      {note && (
        <div className="note">
          <Icon name={`state-${state ?? 'warn'}`} size={12} />
          {note}
        </div>
      )}
    </div>
  )
}

/**
 * Metered hours (transcoding, captions): role="meter" with the minutes as numbers, the texts from
 * the caller. `sm` is the compact variant of the Studio aside and the OBS dock.
 */
export function Quota({ label, icon, used, max, value, of, left, sub, state, sm }: { label: string; icon?: IconName; used: number; max: number; value: ReactNode; of: ReactNode; left: ReactNode; sub?: ReactNode; state?: 'warn' | 'fail'; sm?: boolean }) {
  const pct = max > 0 ? Math.min(100, (used / max) * 100) : 0
  return (
    <div className={sm ? 'sx-quota sm' : 'sx-quota'} data-state={state} role="meter" aria-label={label} aria-valuemin={0} aria-valuemax={max} aria-valuenow={Math.min(used, max)}>
      {!sm && icon && <Icon name={icon} />}
      <span className="nm">{label}</span>
      <span className="val">
        {value} <small>{of}</small>
      </span>
      <div className="bar">
        <b style={{ width: `${pct.toFixed(1)}%` }} />
      </div>
      <div className="sub">
        <span className="left">{left}</span>
        {sub && <span>{sub}</span>}
      </div>
    </div>
  )
}

/** "28 min left" next to a control: the word carries it, the icon only at warn/fail. */
export function Left({ state, children }: { state?: 'warn' | 'fail'; children: ReactNode }) {
  return (
    <span className="sx-left" data-state={state}>
      {state && <Icon name={`state-${state}`} size={12} />}
      {children}
    </span>
  )
}
